import { useEffect, useMemo, useState } from "react";
import { Search, Phone } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { http, PHONE, PHONE_DISPLAY, carTitle } from "../../lib/api";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { CarCard } from "./CarCard";

const selectCls = "h-12 rounded-full border border-ink/10 bg-white px-5 text-sm text-ink outline-none transition-colors focus:border-champagne";

export const Inventory = () => {
  const { t } = useI18n();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [make, setMake] = useState("");
  const [fuel, setFuel] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    http.get("/cars")
      .then((r) => setCars(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCars([]))
      .finally(() => setLoading(false));
  }, []);

  const makes = useMemo(() => [...new Set(cars.map((c) => c.make).filter(Boolean))].sort(), [cars]);
  const fuels = useMemo(() => [...new Set(cars.map((c) => c.fuel).filter(Boolean))], [cars]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    let r = cars.filter((c) => (!make || c.make === make) && (!fuel || c.fuel === fuel)
      && (!s || `${carTitle(c)} ${c.version || ""}`.toLowerCase().includes(s)));
    const by = {
      priceAsc: (a, b) => (a.price ?? Infinity) - (b.price ?? Infinity),
      priceDesc: (a, b) => (b.price ?? -1) - (a.price ?? -1),
      kmAsc: (a, b) => (a.km ?? Infinity) - (b.km ?? Infinity),
    }[sort];
    return by ? [...r].sort(by) : r;
  }, [cars, q, make, fuel, sort]);

  return (
    <section id="inventory" className="relative mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-28" data-testid="inventory-section">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <Reveal>
          <Eyebrow>{t.inv.eyebrow}</Eyebrow>
          <SectionTitle title={t.inv.title} accent={t.inv.titleAccent} />
        </Reveal>
        <Reveal delay={0.1} className="font-mono text-xs uppercase tracking-[0.24em] text-slate-600">
          <span className="font-display text-4xl font-extrabold text-ink" data-testid="inventory-count">{String(list.length).padStart(2, "0")}</span> {t.inv.count}
        </Reveal>
      </div>

      {cars.length > 0 && (
        <Reveal delay={0.1} className="mt-12 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <label className="relative flex-1 sm:min-w-[260px]">
            <Search className="absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.inv.search} data-testid="inventory-search-input"
              className={`${selectCls} w-full pl-12`} />
          </label>
          <select value={make} onChange={(e) => setMake(e.target.value)} className={selectCls} data-testid="inventory-filter-make">
            <option value="">{t.inv.allMakes}</option>
            {makes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={fuel} onChange={(e) => setFuel(e.target.value)} className={selectCls} data-testid="inventory-filter-fuel">
            <option value="">{t.inv.allFuels}</option>
            {fuels.map((f) => <option key={f} value={f}>{t.fuel[f] || f}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls} data-testid="inventory-sort">
            {Object.entries(t.inv.sort).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Reveal>
      )}

      {loading ? (
        <div className="mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-[22px] bg-ink/5" />)}
        </div>
      ) : cars.length === 0 ? (
        <Reveal className="mt-14 flex flex-col items-start justify-between gap-6 rounded-[28px] border border-dashed border-ink/15 bg-white p-8 sm:flex-row sm:items-center sm:p-12" data-testid="inventory-empty">
          <p className="max-w-xl font-display text-lg font-bold uppercase leading-tight tracking-tight text-ink sm:text-2xl">{t.inv.empty}</p>
          <a href={`tel:${PHONE}`} className="btn-ink"><Phone className="h-4 w-4" /> {PHONE_DISPLAY}</a>
        </Reveal>
      ) : list.length === 0 ? (
        <div className="mt-14 flex items-center gap-4 text-slate-600" data-testid="inventory-no-results">
          {t.inv.noResults}
          <button className="link-underline text-ink" onClick={() => { setQ(""); setMake(""); setFuel(""); }} data-testid="inventory-reset">{t.inv.reset}</button>
        </div>
      ) : (
        <div className="mt-14 grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3" data-testid="inventory-grid">
          {list.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.08} className={i % 3 === 1 ? "lg:translate-y-16" : ""}>
              <CarCard car={c} index={i} />
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
};
